import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';
import useAuthStore from '../store/authStore';

const YearContext = createContext();

export const YearProvider = ({ children }) => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [yearResolved, setYearResolved] = useState(false);
  // Year list is tenant-scoped — calling /settings/years before login
  // produced a 401 + Cloud Run access-log WARN on every fresh page load.
  // Gate the fetch on a present auth token; re-run when login completes.
  const token = useAuthStore((s) => s.token);

  // Fetch years from backend and validate localStorage selection
  useEffect(() => {
    if (!token) {
      // Pre-auth: don't hit a 401. Mark as resolved so consumers downstream
      // don't block waiting for a year that can't load yet.
      setYearResolved(true);
      return;
    }
    const initializeYear = async () => {
      try {
        const response = await api.get('/settings/years');
        const yearsList = Array.isArray(response) ? response : (response?.data || []);
        setYears(yearsList);

        const savedYearId = localStorage.getItem('selectedYearId');

        if (savedYearId && yearsList.some(y => y.id === savedYearId)) {
          // Saved year still exists in backend — use it
          setSelectedYear(savedYearId);
        } else if (yearsList.length > 0) {
          // Saved year is stale/missing — auto-select first
          setSelectedYear(yearsList[0].id);
        }
        // else: no years exist, selectedYear stays null
      } catch (err) {
        console.error('Error fetching years:', err);
        // Fallback: try localStorage even if backend fails
        const savedYearId = localStorage.getItem('selectedYearId');
        if (savedYearId) {
          setSelectedYear(savedYearId);
        }
      } finally {
        setYearResolved(true);
      }
    };

    initializeYear();
  }, [token]);

  // Save selected year to localStorage when it changes
  useEffect(() => {
    if (selectedYear) {
      localStorage.setItem('selectedYearId', selectedYear);
    }
  }, [selectedYear]);

  const value = {
    selectedYear,
    setSelectedYear,
    years,
    setYears,
    loading,
    setLoading,
    yearResolved
  };

  return (
    <YearContext.Provider value={value}>
      {children}
    </YearContext.Provider>
  );
};

export const useYear = () => {
  const context = useContext(YearContext);
  if (!context) {
    throw new Error('useYear must be used within a YearProvider');
  }
  return context;
};
